import React, { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { createScriptFormData, sceneFormData } from '../../../../data/formData';
import BrowseLocationImages from './BrowseLocationImages';
import ShowDetailsStep from './ShowDetailsStep';
import ScenesStep from './ScenesStep';
import { makePostCall } from '../../../../effects/requests';
import { endPoints } from '../../../../utils/endpoints';
import { ApiResponseType } from '../../../../types';
import { emptyFunction, showToast } from '../../../../utils';
import moment from 'moment';
import SuccessCreate from '../../../../global/ui/SuccessCreate';

const CreateScript = ({ productionCompanyName = '', onClose, onSubmit }: any) => {

    const stepOneFormData = useForm({
        mode: 'all',
        defaultValues: { ...createScriptFormData() },
        reValidateMode: 'onBlur',
    });

    const stepTwoFormData = useForm({
        mode: 'all',
        defaultValues: { ...sceneFormData() },
        reValidateMode: 'onBlur',
    });

    const [selectedCoverImg, setSelectedCoverImg] = useState<any>(null);
    const [selectedLocImg, setSelectedLocImg] = useState<any>(null);
    const [showBrowseLoc, setShowBrowseLoc] = useState<any>(false);
    const [step, setStep] = useState(1);
    const [selectedData, setSelectedData] = useState([]);
    const [activeItem, setActiveItem] = useState(1);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const handleCoverFileSelected = (file: any) => {
        setSelectedCoverImg({
            image: URL.createObjectURL(file),
            file: file
        })
    };

    const handleCreateScript = () => {
        const showDetails: any = stepOneFormData.getValues();
        const formData = new FormData();
        const showType = showDetails?.show_type;

        if (selectedData?.length <= 0) {
            showToast(`Please add at least one ${showType === 'tv_show' ? 'episode' : 'scene'}.`, 'error');
            return
        }

        if (['tv_show', 'film', 'advert'].includes(showType)) {

            const commonFields = {
                production_company_name: productionCompanyName,
                show_type: showType,
                name: showDetails?.name,
                // add_locations_to_vault: String(!!showDetails?.add_locations_to_vault),
            };

            Object.entries(commonFields).forEach(([key, value]) => {
                formData.append(key, value ?? '');
            });

            if (selectedCoverImg?.file) formData.append('cover_image', selectedCoverImg?.file || '');

            if (showType === 'tv_show') {
                formData.append('number_of_episodes', String(showDetails?.number_of_episodes ?? ''));
            } else {
                formData.append('number_of_scenes', String(showDetails?.number_of_scenes ?? ''));
            }

            if (showType === 'film' && selectedLocImg?.id != null) {
                formData.append('location_folder_image_id', String(selectedLocImg.id));
            }

            // if (showDetails?.add_locations_to_vault) {
            //     formData.append('create_photo_folders', String(!!showDetails?.create_photo_folders));
            //     formData.append('locations_date', showDetails?.locations_date ? moment(showDetails?.locations_date, 'DD/MM/YYYY').format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'))
            // }
        }

        makePostCall(endPoints.LOCATION_SCRIPT, formData).then((res: ApiResponseType<any>) => {
            if (res?.status?.code == 200) {
                if (selectedData?.length > 0) {
                    createSence(selectedData, res?.data, showType)
                    return
                }
                setShowSuccessModal(true)
            }
        }).catch(emptyFunction)

    };

    const createSence = async (formDataArray: Array<any>, scriptData: any, showType: any) => {
        const apiCalls = formDataArray.map((item) => {
            const payloads: any = {
                script_id: scriptData?.id,
                set_name: item?.scene?.set_name,
                scene_number: item?.scene?.scene_number,
                pages: item?.scene?.pages,
                day_or_night: item?.scene?.day_or_night?.value,
                place_type: item?.scene?.type,
            };

            if (showType === "tv_show") {
                const selectedEpisode = scriptData?.episodes?.find((episode: any) => episode?.number === Number(item?.itemNumber));

                if (selectedEpisode) {
                    payloads.episode_id = selectedEpisode?.id;
                }
            }

            return makePostCall(endPoints.LOCATION_SCRIPT_SCENE, payloads);
        });

        await Promise.allSettled(apiCalls);
        setShowSuccessModal(true);
    };

    if (showBrowseLoc) {
        return <div className="cs-overlay">
            <BrowseLocationImages
                onBack={() => setShowBrowseLoc(false)}
                onAssign={(data: any) => {
                    setSelectedLocImg(data);
                    setShowBrowseLoc(false)
                }}
            />
        </div>
    }

    return (
        <>
            {/* Backdrop */}
            <div className="cl-backdrop" />
            {/* Drawer panel */}
            <div className="cl-drawer">
                {/* Header */}
                <div className="cl-drawer__header">
                    <button className="cl-drawer__close" onClick={onClose}>
                        <img src='/assets/images/dismiss.svg' alt='close' />
                    </button>
                    <h2 className="cl-drawer__title">Create Script</h2>
                </div>
                {/* Step content */}
                <div className="cl-drawer__body">

                    {step == 1 &&
                        <FormProvider {...stepOneFormData}>
                            <ShowDetailsStep
                                onBrowseImages={() => setShowBrowseLoc(true)}
                                onCoverFileSelected={handleCoverFileSelected}
                                fromData={stepOneFormData}
                                selectedCoverImg={selectedCoverImg}
                                selectedLocImg={selectedLocImg}
                                resetOtherData={() => {
                                    setSelectedData([])
                                    setActiveItem(1)
                                }}
                            />
                        </FormProvider>
                    }

                    {step == 2 &&
                        <FormProvider {...stepTwoFormData}>
                            <ScenesStep
                                fromData={stepOneFormData}
                                stepTwoFormData={stepTwoFormData}
                                selectedData={selectedData}
                                setSelectedData={setSelectedData}
                                setActiveItem={setActiveItem}
                                activeItem={activeItem}
                            />
                        </FormProvider>
                    }

                </div>
                {/* Footer: dots + action button */}
                <div className="cl-drawer__footer">
                    <div className="cl-dots">
                        {[1, 2].map((s: any) => (
                            <span
                                onClick={() => {
                                    if (step > s) setStep(s)
                                }}
                                key={s}
                                className={`cl-dot${step === s ? ' cl-dot--active' : ''}`}
                            />
                        ))}
                    </div>
                    <button className="cl-btn-next" onClick={() => step === 1 ? stepOneFormData.handleSubmit(() => setStep(2))() : handleCreateScript()}>
                        {step === 1 ? 'Next' : 'Submit'}
                    </button>
                </div>
            </div>
            {showSuccessModal &&
                <SuccessCreate
                    decription='Successfully created new script.'
                    heading='Script Created'
                    onClose={() => {
                        setShowSuccessModal(false);
                        onSubmit?.();
                    }}
                />
            }
        </>
    );
};

export default CreateScript;